import { Box, Button } from "@mui/material";
import React, { useState } from "react";
import styled from "styled-components";
import { DateDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/DateDataDefinition";

import { IDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { NumberDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/NumberDataDefinition";

import { StringDataDefinition } from "../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { DataDefinition } from "../../rule-engine-core/Common/Data/DataDefinition";

import { DataTypes } from "../../rule-engine-core/Common/Data/DataTypes";

import { UILayer } from "../BuildlingBlocks/UILayer";
import { PropertyEditorBoolean } from "../PropertyEditing/PropertyEditorBoolean";
import { PropertyEditorDate } from "../PropertyEditing/PropertyEditorDate";
import { PropertyEditorInteger } from "../PropertyEditing/PropertyEditorInteger";
import { PropertyEditorText } from "../PropertyEditing/PropertyEditorText";
import { PropertyEditorTextList } from "../PropertyEditing/PropertyEditorTextList";
import { DataBuilder } from "../../DataBuilder/DataBuilder";
import { UISwitch } from "../BuildlingBlocks/UISwitch";
import { UILabel } from "../BuildlingBlocks/UILabel";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";
import { PropertyEditorCollapse } from "../PropertyEditing/PropertyEditorCollapse";
import { UICollapse } from "../BuildlingBlocks/UICollapse";
import { StateManager } from "../../rule-engine-core/Common/StateManager";
import { UIStandardLayer } from "../BuildlingBlocks/UIStandardLayer";

interface IDataDefEditLayer {
    dataDef: IDataDefinition;
    onUpdate: () => void;
    onClose: () => void;
    open: boolean;
}

export const DataDefEditLayer: React.FC<IDataDefEditLayer> = ({
    dataDef,
    onUpdate,
    onClose,
    open,
}: IDataDefEditLayer) => {
    const updateCallback = () => {
        onUpdate();
    };

    let content: JSX.Element | null = null;
    let newDef = new DataDefinition();

    switch (dataDef.type) {
        case DataTypes.STRING:
            content = (
                <StringDataDefEditProperties
                    dataDef={dataDef as StringDataDefinition}
                    updateCallback={updateCallback}
                />
            );
            break;
        case DataTypes.DATE:
            content = (
                <DateDataDefEditProperties
                    dataDef={dataDef as DateDataDefinition}
                    updateCallback={updateCallback}
                />
            );
            break;
        case DataTypes.NUMBER:
            content = (
                <NumberDataDefEditProperties
                    dataDef={dataDef as NumberDataDefinition}
                    updateCallback={updateCallback}
                />
            );
            break;
        case DataTypes.FILE:
            return null;
        case DataTypes.OBJECT:
            newDef.fields = Reflect.get(dataDef, "fields") ?? [];
            content = <DataBuilder definition={newDef} />;
            break;
        default:
            content = <div>Select type</div>;
    }

    if (dataDef.type === DataTypes.OBJECT) {
        return (
            <UILayer
                open={open}
                onClose={onClose}
                showCloseButton
                renderUI={() => {
                    return (
                        <Box
                            display={"flex"}
                            flexDirection={"column"}
                            padding={"20px"}
                        >
                            <Box
                                paddingBottom={"20px"}
                                display={"flex"}
                                flexDirection={"row"}
                                justifyContent={"space-between"}
                            >
                                <UILabel variant={"page_heading"}>Edit Data Definition</UILabel>
                                <Button
                                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                                        event.stopPropagation();
                                        Reflect.set(dataDef, "fields", newDef.fields);
                                        onUpdate();
                                        onClose();
                                    }}
                                    sx={{ background: UIStyleConstants.BLUE_COLOR }}
                                    variant="contained"
                                >
                                    Save
                                </Button>
                            </Box>
                            {content}
                        </Box>
                    );
                }}
            />
        );
    }

    return (
        <UIStandardLayer
            title={"Edit " + dataDef.type + " Settings"}
            onClose={onClose}
            open={open}
            primaryActionText={"Save"}
            onPrimaryAction={() => {
                onUpdate();
                onClose();
            }}
            renderUI={() => {
                return <DataDefEditLayerRender>{content}</DataDefEditLayerRender>;
            }}
        ></UIStandardLayer>
    );
};

const DataDefEditLayerRender = styled.div`
    display: flex;
    background: ${UIStyleConstants.WHITE_COLOR};
    z-index: 2;
    width: 300px;
    flex-direction: column;
    padding: 8px 20px;
`;

interface IStringDataDefEditProperties {
    dataDef: StringDataDefinition;
    updateCallback: () => void;
}

const StringDataDefEditProperties = ({ dataDef, updateCallback }: IStringDataDefEditProperties) => {
    const [validValues, setValidValues] = useState<string[]>(dataDef.validValues);
    const [pattern, setPattern] = useState<string | undefined>(dataDef.pattern);

    return (
        <Box
            paddingTop={"15px"}
            display={"flex"}
            flexDirection={"column"}
        >
            <Box
                display={"flex"}
                justifyContent={"space-between"}
            >
                <UILabel>Specify Valid Values</UILabel>
                <UISwitch
                    value={validValues.length > 0}
                    trueDisplay={"Yes"}
                    falseDisplay={"No"}
                    onChange={(newValue) => {
                        if (newValue) {
                            dataDef.validValues.push("");
                        } else {
                            dataDef.validValues = [];
                        }
                        setValidValues([...dataDef.validValues]);
                        updateCallback();
                        StateManager.propertyChanged(dataDef, "validValues");
                    }}
                />
            </Box>
            <Box paddingTop={"10px"}></Box>
            <PropertyEditorCollapse
                editObject={dataDef}
                collapseLogicOrField={() => validValues.length > 0}
                observeProperties={["validValues"]}
            >
                <PropertyEditorTextList
                    editObject={dataDef}
                    propertyName="validValues"
                    label="Valid Values"
                    onChange={() => {
                        setValidValues(dataDef.validValues);
                        updateCallback();
                    }}
                />
                <Box paddingTop={"10px"}></Box>
            </PropertyEditorCollapse>
            <Box
                display={"flex"}
                justifyContent={"space-between"}
            >
                <UILabel>Specify Pattern</UILabel>
                <UISwitch
                    value={pattern !== undefined}
                    trueDisplay={"Yes"}
                    falseDisplay={"No"}
                    onChange={(newValue) => {
                        if (newValue) {
                            dataDef.pattern = "";
                        } else {
                            dataDef.pattern = undefined;
                        }
                        setPattern(dataDef.pattern);
                        updateCallback();
                        StateManager.propertiesChanged(dataDef, ["pattern"]);
                    }}
                />
            </Box>
            <Box paddingTop={"10px"}></Box>
            <UICollapse in={pattern !== undefined}>
                <PropertyEditorText
                    editObject={dataDef}
                    propertyName="pattern"
                    onChange={() => {
                        setPattern(dataDef.pattern);
                        updateCallback();
                    }}
                    label="Pattern"
                    hint="specify regular expression"
                />
                <Box paddingTop={"10px"}></Box>
            </UICollapse>
        </Box>
    );
};

interface IDateDataDefEditProperties {
    dataDef: DateDataDefinition;
    updateCallback: () => void;
}

const DateDataDefEditProperties = ({ dataDef, updateCallback }: IDateDataDefEditProperties) => {
    const [storeTime, setStoreTime] = useState<boolean>(dataDef.storeTime);
    // eslint-disable-next-line
    const [_, setLowerBounds] = useState<Date | undefined>(dataDef.lowerBounds);
    // eslint-disable-next-line
    const [__, setUpperBounds] = useState<Date | undefined>(dataDef.upperBounds);

    return (
        <Box
            display={"flex"}
            flexDirection={"column"}
            paddingTop={"15px"}
        >
            <PropertyEditorBoolean
                label="Store Time"
                editObject={dataDef}
                propertyName="storeTime"
                onChange={() => {
                    setStoreTime(dataDef.storeTime);
                    updateCallback();
                }}
            />

            <Box paddingTop={"15px"}></Box>

            <PropertyEditorDate
                editObject={dataDef}
                propertyName="lowerBounds"
                label="Lower Bounds"
                storeTime={storeTime}
                onChange={() => {
                    setLowerBounds(dataDef.lowerBounds);
                    updateCallback();
                }}
            />
            <Box paddingTop={"15px"}></Box>

            <PropertyEditorDate
                editObject={dataDef}
                propertyName="upperBounds"
                label="Upper Bounds"
                storeTime={storeTime}
                onChange={() => {
                    setUpperBounds(dataDef.upperBounds);
                    updateCallback();
                }}
            />
            <Box paddingTop={"15px"}></Box>
        </Box>
    );
};

interface INumberDataDefEditProperties {
    dataDef: NumberDataDefinition;
    updateCallback: () => void;
}

const NumberDataDefEditProperties = ({ dataDef, updateCallback }: INumberDataDefEditProperties) => {
    const [allowDecimals, setAllowDecimals] = useState<boolean>(dataDef.allowDecimals);
    // eslint-disable-next-line
    const [_, setLowerBounds] = useState<number | undefined>(dataDef.lowerBounds);
    // eslint-disable-next-line
    const [__, setUpperBounds] = useState<number | undefined>(dataDef.upperBounds);

    return (
        <Box
            display={"flex"}
            flexDirection={"column"}
            paddingTop={"15px"}
        >
            <PropertyEditorBoolean
                label="Allow Decimals"
                editObject={dataDef}
                propertyName="allowDecimals"
                onChange={() => {
                    setAllowDecimals(dataDef.allowDecimals);
                    updateCallback();
                }}
            />
            <Box paddingTop={"15px"}></Box>
            <PropertyEditorInteger
                editObject={dataDef}
                propertyName="lowerBounds"
                decimal={allowDecimals}
                onChange={() => {
                    setLowerBounds(dataDef.lowerBounds);
                    updateCallback();
                }}
                label="Lower Bounds"
            />
            <Box paddingTop={"15px"}></Box>
            <PropertyEditorInteger
                editObject={dataDef}
                propertyName="upperBounds"
                decimal={allowDecimals}
                onChange={() => {
                    setUpperBounds(dataDef.upperBounds);
                    updateCallback();
                }}
                label="Upper Bounds"
            />
            <Box paddingTop={"15px"}></Box>
        </Box>
    );
};
